import React, { useEffect, useRef, useState } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNull from 'lodash/isNull';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import withSizes from 'react-sizes';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {
  getAnswersByAccessCode,
  getAssessmentResult,
  getAssessment,
  setAnswersSource,
} from 'containers/Assessment/actions';
import {
  makeSelectAnswers,
  makeSelectAnswersSource,
  makeSelectAssessmentResult,
  makeSelectAssessment,
} from 'containers/Assessment/selectors';
import { makeSelectAuth } from 'containers/Auth/selectors';
import AppContainer from 'components/AppContainer';
import ArrowIcon from 'components/BackHeader/images/arrow-back.svg';
import useSiteCopySelector from 'components/useSiteCopySelector';
import PageSkeleton from 'containers/AssessmentResult/components/PageSkeleton';
import html2pdf from 'html2pdf.js';
import Header from 'containers/AssessmentResult/components/Header';
import { getImageFile } from 'utils/stringUtils';
import HorizontalProgress from 'components/SurveyResult/components/HorizontalProgress';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import Divider from 'components/Divider';
import Mixpanel from 'utils/mixpanelService';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import AccessCodeButton from './components/AccessCodeButton';
import AssessmentClinicalResultDetail from '../ResultDetail';
import DownloadIcon from '../images/download.svg';
import UpArrowIcon from '../images/up-arrow.svg';
import DownArrowIcon from '../images/down-arrow.svg';

const useStyles = makeStyles(theme => ({
  root: {
    background: '#f8f9f9',
    height: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewButton: {
    padding: 12,
    borderRadius: 5,
    background: '#FFFFFF',
    color: '#5f5f5f',
    border: `1px solid #e6e6e6`,
    fontFamily: 'MadaRegular',
    fontSize: 'medium',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    columnGap: ({ isMobile }) => isMobile && '5px',
    '&:hover': {
      opacity: 0.8,
    },
    '& img': {
      marginRight: ({ isMobile }) => (isMobile ? 0 : '10px'),
      color: '#5f5f55',
    },
  },
  gridContainer: {
    background: '#FFFFFF',
    marginTop: 20,
    padding: 20,
  },
  topHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 40,
  },
  downloadPDFButton: {
    padding: 12,
    borderRadius: 5,
    background: '#FFFFFF',
    color: '#5f5f5f',
    border: `1px solid #e6e6e6`,
    fontFamily: 'MadaRegular',
    fontSize: 'medium',
    '&:hover': {
      opacity: 0.8,
    },
    '& img': {
      marginRight: '10px',
      color: '#5f5f55',
    },
  },
  heading: {
    color: '#4c4c4c',
    fontSize: ({ isMobile }) => isMobile && '1rem',
  },
  tableHeaderContainer: {
    marginBottom: 0,
    background: '#f8f9f9',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderTop: '1px solid #e6e6e6',
    borderLeft: '1px solid #e6e6e6',
    borderRight: '1px solid #e6e6e6',
  },
  tableHeader: {
    border: '1px solid #e6e6e6',
    padding: 17,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiTypography-h6': {
      fontSize: '0.8rem',
    },
  },
  icon: {
    height: 8,
    width: 8,
    borderRadius: 4,
    display: 'inline-block',
    marginRight: 6,
  },
  contentContainer: {
    display: ({ isMobile }) => (!isMobile ? 'flex' : 'block'),
    marginBottom: ({ isMobile }) => (!isMobile ? 0 : 20),
  },

  contentData: {
    border: ({ isMobile }) => (!isMobile ? '1px solid #e6e6e6' : '0px'),
    padding: ({ isMobile }) => (!isMobile ? 17 : 5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: ({ isMobile }) => (!isMobile ? 600 : 400),
    },
    '& .MuiTypography-body1': {
      fontSize: '1rem',
    },
  },
  mobileViewContainer: {
    display: 'flex',
    width: '90%',
    justifyContent: 'space-between',
  },
  headerTypo: {
    marginRight: 40,
    '& .MuiTypography-h6': {
      fontSize: '0.9rem',
    },
  },
  nameTypo: {
    fontSize: ({ isMobile }) => isMobile && '1.2rem',
    marginBottom: ({ isMobile }) => (isMobile ? 10 : 0),
  },
  sourceGrid: {
    marginTop: 30,
  },
  sourceBody: {
    marginTop: 20,
    fontSize: '1rem',
  },
  chartWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  accordionLabel: {
    fontSize: 22,
    width: '100%',
    fontFamily: 'MadaMedium',
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
    },
  },
  chartContainer: {
    margin: 10,
    marginBottom: 30,
  },
}));

const AssessmentClinicalResults = ({ isMobile }) => {
  const { code } = useParams();
  const classes = useStyles({ isMobile });
  const dispatch = useDispatch();
  const history = useHistory();
  const shouldResetAnswersSource = useRef(true);
  const [siteCopy] = useSiteCopySelector(['assessment-clinical-view']);
  const answers = useSelector(makeSelectAnswers());
  const assessment = useSelector(makeSelectAssessment());
  const answersSource = useSelector(makeSelectAnswersSource());
  const assessmentResult = useSelector(makeSelectAssessmentResult());
  const auth = useSelector(makeSelectAuth());
  const clinicMode = useSelector(state => state.main.clinicMode);
  const [hideContent, setHideContent] = useState([]);
  const [visiblePdfClinicalContent, setvisiblePdfClinicalContent] = useState(
    false,
  );
  const clientDetails = useSelector(makeSelectClientDetails());
  const hideDownloadPdfButton =
    _get(clientDetails, 'metadata.hidePdfDownloadButton', false) || clinicMode;
  const hasTracked = useRef(false);

  const slug = _get(answers, 'assessment.slug', '');

  useEffect(
    () => () => {
      if (
        ['session', 'homeAccessCode'].includes(answersSource) &&
        shouldResetAnswersSource.current
      ) {
        dispatch(setAnswersSource(null));
      }
    },
    [dispatch, answersSource],
  );

  useEffect(() => {
    if (!_isEmpty(assessment) && !hasTracked.current) {
      Mixpanel.track('Assessment Clinical Results Page Viewed', {
        assessment: _get(assessment, 'items.0.fields.name'),
        entryId: _get(assessment, 'items.0.sys.id'),
      });
      hasTracked.current = true;
    }
  }, [assessment]);

  useEffect(() => {
    if (slug && _get(assessment, 'items.0.fields.slug') !== slug) {
      dispatch(getAssessment(slug));
    }
  }, [assessment, dispatch, slug]);

  useEffect(() => {
    if (auth.isLoaded && code && answersSource !== 'homeAccessCode') {
      dispatch(
        getAnswersByAccessCode({ accessCode: code, view: 'clinical view' }),
      );
    }
  }, [dispatch, answersSource, auth, code]);

  useEffect(() => {
    if (auth.isLoaded && !_isEmpty(answers)) {
      dispatch(
        getAssessmentResult({
          slug: answers.assessment.slug,
          answers: answers.result,
        }),
      );
    }
  }, [answers, auth, dispatch, history]);

  const scoreComponents = _get(
    assessmentResult,
    'scoringsObject.regularComponents',
    [],
  );

  const scoringObject = _get(assessmentResult, 'scoringsObject.scoring', []);
  const scoringResultObject = _get(assessmentResult, 'results.items', []);
  const columnHeaders = _get(siteCopy, 'pageCopy.summaryColumnHeaders', []);

  const handleClinicalPdfContent = value => {
    setvisiblePdfClinicalContent(value);
  };

  const handlePdfDownload = async () => {
    handleClinicalPdfContent(true);

    const doc = document.getElementById('assessment-clinical-result');
    const opt = {
      margin: [0, 0, 0, 0],
      filename: `${slug}-clinical-${code}.pdf`,
      image: { type: 'jpg', quality: 0.98 },
      html2canvas: {
        dpi: 192,
        scale: 1.2,
        allowTaint: false,
        useCORS: true,
        foreignObjectRendering: false,
        letterRendering: true,
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      jsPDF: { unit: 'pt', format: 'letter', orientation: 'portrait' },
    };
    await html2pdf()
      .set(opt)
      .from(doc)
      .save();

    handleClinicalPdfContent(false);
  };

  const tableHeaderContent = (
    <Grid container className={classes.tableHeaderContainer}>
      <Grid item xs={5} className={classes.tableHeader}>
        <Typography variant="h6" color="textPrimary">
          {columnHeaders.resultComponent}
        </Typography>
      </Grid>
      <Grid item xs={2} className={classes.tableHeader}>
        <Typography variant="h6" color="textPrimary">
          {columnHeaders.resultScore}
        </Typography>
      </Grid>
      <Grid item xs={3} className={classes.tableHeader}>
        <Typography variant="h6" color="textPrimary">
          {columnHeaders.resultRange}
        </Typography>
      </Grid>
      <Grid item xs={2} className={classes.tableHeader}>
        <Typography variant="h6" color="textPrimary">
          {columnHeaders.assessmentResponse}
        </Typography>
      </Grid>
    </Grid>
  );

  const handlePatientViewClick = () => {
    shouldResetAnswersSource.current = false;
    history.push(`/assessments/${answers.assessment.slug}/results`);
  };

  const handleClick = label => {
    setHideContent(prev =>
      prev.length > 0
        ? prev.filter(cnt => cnt.label === label).length > 0
          ? prev.filter(cnt => cnt.label !== label)
          : [...hideContent, { label, open: true }]
        : [...hideContent, { label, open: true }],
    );
  };

  const getIntervals = (data, index) => {
    const interval = _get(data, 'intervals', []).filter(
      intervl =>
        scoringObject[index] >= intervl.start &&
        scoringObject[index] <= intervl.end,
    );
    return interval;
  };
  const validScoreComponents = scoreComponents.filter(
    (comp, index) => !_isEmpty(getIntervals(comp, index)),
  );

  const groupComponents = scoreComponents_ =>
    ['bad', 'medium', 'good'].flatMap(id =>
      scoreComponents_.filter(({ summaryId }) => summaryId === id),
    );
  const validatedComponents = groupComponents(
    scoreComponents.reduce((acc, data, index) => {
      const interval = getIntervals(data, index);

      if (_isEmpty(interval)) return acc;

      // add scoring of component to data
      acc.push({
        ...data,
        score: scoringObject[index],
        interval: {
          label: interval[0].label,
          color: interval[0].color,
        },
      });

      return acc;
    }, []),
  );

  const content = data => {
    if (!data) return null;

    return (
      <Grid>
        <Grid container className={classes.contentContainer}>
          <Grid item xs={isMobile ? 12 : 5} className={classes.contentData}>
            <Typography
              color="textPrimary"
              variant={`${
                hideContent.filter(cnt => cnt.open && cnt.label === data.name)
                  .length > 0 || !isMobile
                  ? 'h6'
                  : 'body'
              }`}
              className={classes.nameTypo}
            >
              {data.name}
            </Typography>
            {isMobile && (
              <ButtonBase onClick={() => handleClick(data.name)}>
                <img
                  alt="down-arrow"
                  src={
                    hideContent.filter(
                      cnt => cnt.open && cnt.label === data.name,
                    ).length > 0
                      ? DownArrowIcon
                      : UpArrowIcon
                  }
                />
              </ButtonBase>
            )}
          </Grid>
          {(hideContent.filter(cnt => cnt.open && cnt.label === data.name)
            .length > 0 ||
            !isMobile) && (
            <Grid item xs={isMobile ? 12 : 2} className={classes.contentData}>
              <Grid className={isMobile && classes.mobileViewContainer}>
                {isMobile && (
                  <Typography
                    variant="h6"
                    color="textPrimary"
                    className={classes.headerTypo}
                  >
                    {columnHeaders.resultScore}
                  </Typography>
                )}
                <Typography color="textPrimary">
                  {`${data.score} of ${data.maxValue}`}
                </Typography>
              </Grid>
            </Grid>
          )}
          {(hideContent.filter(cnt => cnt.open && cnt.label === data.name)
            .length > 0 ||
            !isMobile) && (
            <Grid item xs={isMobile ? 12 : 3} className={classes.contentData}>
              <Grid className={isMobile && classes.mobileViewContainer}>
                {isMobile && (
                  <Typography
                    variant="h6"
                    color="textPrimary"
                    className={classes.headerTypo}
                  >
                    {columnHeaders.resultRange}
                  </Typography>
                )}
                <Typography
                  style={{
                    color: isMobile && data.interval.color,
                    textAlign: 'end',
                  }}
                >
                  {!isMobile && (
                    <span
                      className={classes.icon}
                      style={{
                        backgroundColor: data.interval.color,
                      }}
                    />
                  )}
                  {data.interval.label}
                </Typography>
              </Grid>
            </Grid>
          )}
          {(hideContent.filter(cnt => cnt.open && cnt.label === data.name)
            .length > 0 ||
            !isMobile) && (
            <Grid item xs={isMobile ? 12 : 2} className={classes.contentData}>
              <Grid className={isMobile && classes.mobileViewContainer}>
                {isMobile && (
                  <Typography
                    variant="h6"
                    color="textPrimary"
                    className={classes.headerTypo}
                  >
                    {columnHeaders.assessmentResponse}
                  </Typography>
                )}
                <Link to={`/assessments/clinicalresults/${code}/${data.name}`}>
                  {' '}
                  View
                </Link>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const fields = _get(assessment, 'items.0.fields', {});
  const name = _get(assessment, 'items.0.fields.name', '');
  const globalOptions = _get(assessment, 'items.0.fields.globalOptions', {});
  const id = _get(assessment, 'items.0.sys.id', id);

  const imageUrl = getImageFile(fields);
  const isFullWidth = _get(globalOptions, 'fullWidth', false);
  const clinicalSources = _get(assessment, 'items.0.fields.clinicalSource', {});

  if (
    !auth.isLoaded ||
    (!auth.isEmpty && code && _isNull(answers)) ||
    _isNull(assessmentResult)
  )
    return (
      <div className={classes.root}>
        <AppContainer justify="flex-start">
          <PageSkeleton isFullWidth />
        </AppContainer>
      </div>
    );

  return (
    <div className={classes.root}>
      <AppContainer justify="flex-start">
        <div id="assessment-clinical-result" style={{ width: '100%' }}>
          <Grid>
            {visiblePdfClinicalContent && (
              <Header
                image={imageUrl}
                title={name}
                isFullWidth={isFullWidth}
                assessmentId={id}
                visiblePdfClinicalContent={visiblePdfClinicalContent}
                answers={answers}
                accessCodeProps={code}
              />
            )}
            <Grid container direction="row">
              {!visiblePdfClinicalContent && (
                <Grid item xs={12} className={classes.buttonContainer}>
                  <ButtonBase
                    className={classes.viewButton}
                    onClick={handlePatientViewClick}
                  >
                    <img alt="back" src={ArrowIcon} />
                    {_get(
                      siteCopy,
                      'pageCopy.patientViewLabel',
                      'Patient view',
                    )}
                  </ButtonBase>
                  <Typography
                    color="textPrimary"
                    variant={`${isMobile ? 'h5' : 'h4'}`}
                    className={classes.heading}
                  >
                    {_get(siteCopy, 'pageCopy.header', 'Clinical Results View')}
                  </Typography>
                  {isMobile && assessmentResult && !hideDownloadPdfButton && (
                    <ButtonBase
                      className={classes.viewButton}
                      onClick={handlePdfDownload}
                    >
                      <img alt="back" src={DownloadIcon} />
                    </ButtonBase>
                  )}
                </Grid>
              )}
              <Grid item xs={12} className={classes.gridContainer}>
                {!visiblePdfClinicalContent && (
                  <Grid className={classes.topHeader}>
                    <AccessCodeButton answer={answers} />

                    {!isMobile && assessmentResult && !hideDownloadPdfButton && (
                      <ButtonBase
                        className={classes.downloadPDFButton}
                        onClick={handlePdfDownload}
                      >
                        <img alt="download" src={DownloadIcon} />
                        {_get(siteCopy, 'pageCopy.downloadPDF', 'Download PDF')}
                      </ButtonBase>
                    )}
                  </Grid>
                )}
                {assessmentResult && (
                  <>
                    {!isMobile && tableHeaderContent}
                    {validatedComponents.map((comp, index) => (
                      <Grid key={comp.name}>{content(comp, index)}</Grid>
                    ))}
                    {visiblePdfClinicalContent && (
                      <Grid className={classes.gridContainer}>
                        {!_isEmpty(validatedComponents) &&
                          validatedComponents.map((data, index) => (
                            <div key={data.name}>
                              {!_isEmpty(getIntervals(data, index)) && (
                                <Grid className={classes.chartContainer}>
                                  <Typography
                                    className={classes.accordionLabel}
                                    gutterBottom
                                  >
                                    {_get(data, 'result.fields.label', '') ||
                                      _get(data, 'resultExpression')}
                                  </Typography>
                                  <div className={classes.chartWrapper}>
                                    <HorizontalProgress
                                      isThermometer={data.isThermometer}
                                      label={
                                        _get(data, 'result.fields.label', '') ||
                                        _get(data, 'resultExpression')
                                      }
                                      min={data.minValue}
                                      max={data.maxValue}
                                      value={scoringObject[index]}
                                      interval={getIntervals(data, index)}
                                      intervals={data.intervals}
                                      scale={data.scale}
                                    />
                                  </div>
                                  <RichTextReactRenderer
                                    data={_get(
                                      scoringResultObject,
                                      `${index}.fields.description`,
                                      '',
                                    )}
                                    paragraphVariant="body2"
                                  />

                                  <Divider
                                    top={isFullWidth ? 32 : 8}
                                    bottom={8}
                                    width="100%"
                                  />
                                </Grid>
                              )}
                            </div>
                          ))}
                      </Grid>
                    )}
                  </>
                )}
                {visiblePdfClinicalContent && (
                  <AssessmentClinicalResultDetail
                    scoreComponents={validScoreComponents}
                    visiblePdfClinicalContent
                  />
                )}
                {!_isEmpty(clinicalSources) && (
                  <Grid className={classes.sourceGrid}>
                    <Typography variant="h5" color="textPrimary">
                      {_get(siteCopy, 'pageCopy.assessmentSource', 'Sources')}
                    </Typography>
                    <RichTextReactRenderer
                      data={clinicalSources}
                      paragraphVariant="body2"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
      </AppContainer>
    </div>
  );
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isTablet: width <= 800,
  isDesktop: width <= 1300,
});

export default withSizes(mapSizesToProps)(AssessmentClinicalResults);
